/* eslint-disable react/no-this-in-sfc */
/* eslint-disable func-names */
/* eslint-disable no-loop-func */
import { makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import * as d3 from 'd3';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import usMapData, { stateMaps, arrangeLabels } from '../modules/maps';
import colors from '../modules/colors';

export default function StateCountyMap({
  data, dataRange,
  width, height, stateCode, colorLow, colorHigh, primaryColor,
  setCounty, countyCode, selectionColor, setCountyCode,
  toolTipFormatterFunction, hideTooltip, legendLabelFormatter,
  legendTitleText = 'The darker the color, the greater the value.',
}) {
  const theme = useTheme();
  const matchesXSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const matchesSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesMedium = useMediaQuery(theme.breakpoints.down('md'));

  const mapRef = useRef(null);
  const legendRef = useRef(null);
  const gradientId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  const tooltipRef = useRef(null);

  const classes = makeStyles((theme) => ({
    toolTip: {
      borderColor: primaryColor,
      border: '1px solid',
      position: 'absolute',
      textAlign: 'left',
      maxWidth: '250px',
      padding: '5px',
      font: '1em "ff-scala-sans-pro", Arial, sans-serif',
      background: '#fff',
      pointerEvents: 'none',
      zIndex: 20,
    },
  }))();

  useEffect(() => {
    async function renderMap() {
      const countyData = stateMaps[stateCode.toUpperCase()]; // await import(`@amcharts/amcharts4-geodata/region/usa/${stateCode.toLowerCase()}High`);
      const counties = countyData.features;

      const svg = d3.select(mapRef.current)
        .attr('width', width)
        .attr('height', height)
        .html(null); // clear svg for re-render on resize

      d3.select(tooltipRef.current).style('opacity', 0);

      // get the state object
      const state = usMapData.features.filter((d) => (d.properties.id === `US-${stateCode.toUpperCase()}`))[0];

      // using fitSize to fit the projection within the viewport
      const projection = d3.geoMercator().fitSize([width, height], state);

      // Define path generator
      const path = d3.geoPath().projection(projection);

      const dr = data.map((d) => (d.value));
      const colorScale = d3.scaleLinear().domain([_.min(dr), _.max(dr)]).range([colorLow, colorHigh]);

      // console.log(dataRange)
      // console.log(`min: ${_.min(dataRange)} max: ${_.max(dataRange)}`);
      // console.log(colorScale(_.mean(dataRange)));

      function populateTooltip(d) {
        const dataPoint = _.find(data, { county_fips: d.id });
        // use provided tooltip formatter function. The parent component will know all about the data.
        if (toolTipFormatterFunction) {
          return toolTipFormatterFunction(d, dataPoint);
        }
        return 'Fix tooltips!';
      }

      // first create paths and attach mouse events
      const countyPaths = svg.append('g')
        .selectAll('path')
        .data(counties)
        .enter()
        .append('path')
        .attr('d', path)
        .on('click', (d) => {
          setCounty && setCounty(d.properties.name);
          setCountyCode && setCountyCode(d.id);
          // if (setCountyData) setCountyData(dataPoint);
        })
        .on('mouseover', (d) => {
          if (!hideTooltip) {
            const dataPoint = _.find(data, { county_fips: d.id });
            const tooltip = d3.select(tooltipRef.current);
            tooltip.transition().duration(300).style('opacity', 1);
            tooltip.html(populateTooltip(d, dataPoint));
          }
        })
        .on('mousemove', () => {
          const tooltip = d3.select(tooltipRef.current);
          return tooltip.style('top', `${d3.event.pageY - 10}px`).style('left', `${d3.event.pageX + 10}px`);
        })
        .on('mouseout', () => {
          const tooltip = d3.select(tooltipRef.current);
          tooltip.transition().duration(300).style('opacity', 0);
        });

      // then add transition and the rest
      countyPaths
        // .transition() // this prevents fill colors from printing
        .style('fill', (d) => {
          const dataPoint = _.find(data, { county_fips: d.id });
          // console.log(dataPoint)
          // if this is a selected county, color it with selectionColor
          if (d.id === countyCode) {
            return selectionColor;
          }

          if (dataPoint && dataPoint.value) {
            return colorScale(dataPoint.value);
          }

          return 'rgb(237, 226, 204)';
        })
        .style('stroke', '#fff')
        .style('stroke-width', '1.5')
        .attr('class', 'county');

      // console.log(theme.breakpoints.down('xs'));
      // console.log(theme.breakpoints.down('sm'));
      // console.log(theme.breakpoints.down('md'));
      if (!matchesXSmall) {
        svg.append('g')
          .selectAll('text')
          .data(counties)
          .enter()
          .append('text')
          .attr('class', 'map-label')
          .text((d) => (d.properties.name))
          .attr('x', (d) => path.centroid(d)[0])
          .attr('y', (d) => path.centroid(d)[1])
          .attr('text-anchor', 'middle')
          .attr('font-size', () => {
            if (matchesSmall) {
              return 'x-small';
            }
            if (matchesMedium) {
              return 'smaller';
            }
            return 14;
          })

          .style('fill', (d) => {
            const dataPoint = _.find(data, { county_fips: d.id });

            if (dataPoint && dataPoint.value) {
              const color = colorScale(dataPoint.value);
              const labelColor = d3.hsl(color).l > 0.5 ? '#000' : '#fff';
              return labelColor;
            }
            return '#000';
          })

          .on('mouseover', (d) => {
            if (!hideTooltip) {
              const tooltip = d3.select(tooltipRef.current);
              tooltip.transition().duration(300).style('opacity', 1);
              tooltip.html(populateTooltip(d));
            }
          })
          .on('mousemove', () => {
            const tooltip = d3.select(tooltipRef.current);
            return tooltip.style('top', `${d3.event.pageY - 10}px`).style('left', `${d3.event.pageX + 10}px`);
          })
          .on('mouseout', () => {
            const tooltip = d3.select(tooltipRef.current);
            tooltip.transition().duration(300).style('opacity', 0);
          });

        // eslint-disable-next-line no-use-before-define
        arrangeLabels(svg);
      } // if(theme.breakpoints.down('xs'))

      // legend
      const legendSizing = {
        padding: 5,
        marginLeft: 20,
        marginRight: 20,
        boxWidth: 54,
      };

      const legendSvg = d3.select(legendRef.current)
        .attr('width', width)
        .attr('height', 120)
        .style('margin-top', 20) // put some space between the legend and the map
        .html(null); // clear svg for re-render on resize

      legendSvg.append('foreignObject')
        .attr('x', 0)
        .attr('y', 0)
        .attr('height', 40)
        .attr('width', width)
        .append('xhtml:div')
        .style('height', '40px')
        .style('width', `${width}px`)
        .style('display', 'flex')
        .style('justify-content', 'center')
        .style('align-items', 'flex-end')
        .style('text-align', 'center')
        .style('font-family', '"ff-scala-sans-pro", Arial, sans-serif')
        .style('font-size', '1em')
        .style('font-weight', 'bold')
        .html(legendTitleText);

      const legendG = legendSvg.append('g')
        .attr('class', 'legendLinear')
        .attr('transform', 'translate(0,60)');

      const rectWidth = legendSizing.boxWidth;
      const rectHeight = rectWidth / 1.61; // golden ratio

      const novalueG = legendG.append('g')
        .attr('width', rectWidth)
        .attr('height', rectHeight + 40) // some space for the label
        .attr('x', 0);

      novalueG.append('rect')
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .attr('x', 0)
        .style('fill', colors.taupe);

      novalueG
        .append('text')
        .attr('class', 'legend-label')
        .attr('x', 0)
        .attr('y', (d) => (rectHeight + 20))
        .attr('text-anchor', 'start')
        .style('font', '1em "ff-scala-sans-pro", Arial, sans-serif')
        .text('No data');

      // Append a defs (for definition) element to your SVG
      const defs = legendSvg.append('defs');

      // Append a linearGradient element to the defs and give it a unique id
      const linearGradient = defs.append('linearGradient')
        .attr('id', `${gradientId}-gradient`);

      // Horizontal gradient
      linearGradient
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%');

      // Set the color for the start (0%)
      linearGradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', colorLow);

      // Set the color for the end (100%)
      linearGradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', colorHigh);

      const gradientWidth = width - rectWidth - legendSizing.padding;
      const gradientX = rectWidth + legendSizing.padding;
      legendG.append('rect')
        .attr('width', gradientWidth)
        .attr('height', rectHeight)
        .attr('x', gradientX)
        .style('fill', `url(#${gradientId}-gradient)`);

      // use this scale to map values to the position on the gradient
      const labelPositionScale = d3.scaleLinear().domain([_.min(dr), _.max(dr)]).range([gradientX, gradientX + gradientWidth]);

      const legendAxis = d3.axisBottom(labelPositionScale);
      legendAxis.tickFormat(legendLabelFormatter);
      // legendAxis.tickSize(10);

      legendG.append('g')
        .style('font', '1em "ff-scala-sans-pro", Arial, sans-serif')
        .attr('transform', `translate(0,${rectHeight})`)
        .attr('width', gradientWidth)
        .call(legendAxis)
        .call((g) => g.select('.domain').remove()); // remove axis line
    }

    renderMap();
  }, [width, stateCode, data, dataRange, countyCode]);

  return (
    <>
      <svg ref={mapRef} />
      <svg ref={legendRef} />
      <div ref={tooltipRef} className={classes.toolTip} />
    </>
  );
}

StateCountyMap.propTypes = {
  height: PropTypes.number,
  width: PropTypes.number,
  stateCode: PropTypes.string,
};

StateCountyMap.defaultProps = {
  height: 100,
  width: 200,
  stateCode: 'NY',
};
